import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  useInfiniteQuery,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  CompanyEntriesSchema,
  CompanyListSchema,
  CompanyOrientationsSchema,
  type CompanySchema,
  CustomTagWithCompanySchema,
  EngineerCompanyListSchema,
  type EngineerCompanySchema,
  type FollowRankingListTermType,
  GetCompanyMasterSchema,
  GetCustomTagsSchema,
  GetFollowCompanyRankingListSchema,
  GetGroupedCompanyMasterSchema,
  GetGroupedSupportCompanyMasterSchema,
  GetIndustryCategoryMasterSchema,
  GetIndustryMasterSchema,
  TopCompanyListSchema,
} from '../entities/company/schema';
import {
  FollowAdCompanyListSchema,
  UsersMeFollowedCompaniesResponse,
} from '../entities/usersMe/schema';
import {
  getCompaniesFromPages,
  getEngineerCompaniesFromPages,
} from '../factories/company/getCompaniesFromPage';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';
import {
  GetWorkAttractiveConditionsSchema,
  type GetWorkAttractiveConditionsType,
} from './../entities/company/schema';

/**
 * GetFollowCompanyRanking
 */
type GetFollowCompanyRankingListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  limit?: number;
  industryCategoryID?: number;
  industryIDs?: number[];
  isEngineerCompany?: boolean;
  term?: FollowRankingListTermType;
  isLastYear?: boolean;
};

export type GetFollowCompanyRankingListResponse = z.infer<
  typeof GetFollowCompanyRankingListSchema
>;

export type ResultGetFollowCompanyRankingList = ResultWithOauthToken<
  GetFollowCompanyRankingListResponse,
  AppError
>;

export const getFollowCompanyRankingList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  ...queryParams
}: GetFollowCompanyRankingListArgs): Promise<ResultGetFollowCompanyRankingList> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetFollowCompanyRankingListResponse, AppError>> => {
    if (accessToken) {
      defaultClient.setToken(accessToken);
    }
    const path = createPathWithQuery(
      `/${version}/companies/follow/ranking/list`,
      {
        ...queryParams,
      },
      'none',
    );
    return defaultClient.get(path, GetFollowCompanyRankingListSchema);
  };

  const result = await sendApiRequest<GetFollowCompanyRankingListResponse>({
    request,
    cookies,
    authRequired: false,
  });

  return result;
};

type UseGetFollowCompanyRankingListOptions = {
  config?: UseQueryOptions<ResultGetFollowCompanyRankingList>;
} & Omit<GetFollowCompanyRankingListArgs, 'cookies'>;

export const useGetFollowCompanyRankingList = ({
  config,
  version,
  ...queryParams
}: UseGetFollowCompanyRankingListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultGetFollowCompanyRankingList>({
    ...config,
    queryKey: ['getFollowCompanyRankingList', {...queryParams}],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getFollowCompanyRankingList({
        cookies,
        version,
        ...queryParams,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetCompanyList
 */

export type Company = z.infer<typeof CompanySchema>;
export type GetCompanyListResponse = z.infer<typeof CompanyListSchema>;
export type ResultGetCompanyList = ResultWithOauthToken<
  GetCompanyListResponse,
  AppError
>;

type GetCompanyListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  queryParams: {
    limit: number;
    page: number;
    order?: 'pageType';
    industryIDs: number[];
    industryCategoryIDs: number[];
    cacheID: string;
    minEmployeeCount?: number;
    maxEmployeeCount?: number;
    workAttractiveConditionIDs?: number[];
  };
};

export const getCompanyList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetCompanyListArgs): Promise<ResultGetCompanyList> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetCompanyListResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/companies`,
      {...queryParams},
      'none',
    );
    return defaultClient.get(path, CompanyListSchema);
  };

  const result = await sendApiRequest<GetCompanyListResponse>({
    request,
    cookies,
    authRequired: false,
  });

  return result;
};

export type GetIndustryCategoryMaster = z.infer<
  typeof GetIndustryCategoryMasterSchema
>;
export type ResultGetIndustryCategoryMaster = Result<
  GetIndustryCategoryMaster,
  AppError
>;

type GetIndustryCategoryMasterParams = {
  version?: string;
  queryParams: {
    q?: string;
    limit?: number;
  };
};

export const getIndustryCategoryMaster = async ({
  version = DEFAULT_API_VERSION,
  queryParams: {q, limit},
}: GetIndustryCategoryMasterParams): Promise<ResultGetIndustryCategoryMaster> => {
  const params = {
    q: q ?? '',
    ...(limit ? {limit} : {}),
  };
  const path = createPathWithQuery(`/${version}/industry-categories`, params);
  const result: Result<GetIndustryCategoryMaster, AppError> =
    await defaultClient.get(path, GetIndustryCategoryMasterSchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetCompanyListOptions = {
  config?: UseQueryOptions<ResultGetCompanyList>;
} & Omit<GetCompanyListArgs, 'cookies'>;

export const useGetCompanyList = ({
  queryParams,
  config,
}: UseGetCompanyListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultGetCompanyList>({
    ...config,
    queryKey: ['companies'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getCompanyList({cookies, queryParams});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

type UseGetIndustryCategoryMasterOptions = {
  config?: UseQueryOptions<ResultGetIndustryCategoryMaster>;
} & GetIndustryCategoryMasterParams;

export const useGetIndustryCategoryMaster = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: UseGetIndustryCategoryMasterOptions) => {
  return useQuery<ResultGetIndustryCategoryMaster>({
    ...config,
    queryKey: ['getIndustryCategoryMaster', queryParams.q, queryParams.limit],
    async queryFn() {
      return getIndustryCategoryMaster({version, queryParams});
    },
  });
};

export const useInfiniteCompanyList = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: UseGetCompanyListOptions): UseInfiniteQueryResult<
  GetCompanyListResponse,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useInfiniteQuery(
    ['companyList', queryParams],
    async ({pageParam = {page: 1, cacheID: ''}}) => {
      const cookies = parseCookies();
      const result = await getCompanyList({
        cookies,
        version,
        queryParams: {
          ...queryParams,
          page: pageParam.page,
          cacheID: pageParam.cacheID,
        },
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return getCompaniesFromPages(result);
    },
    {
      getNextPageParam: (lastPage) => ({
        page: lastPage?.nextURLParam?.page ?? 0,
        cacheID: lastPage?.nextURLParam?.cacheID ?? '',
      }),
      enabled: config?.enabled,
    },
  );
};

// 契約企業一覧
export type GetGroupedSupportCompanies = z.infer<
  typeof GetGroupedSupportCompanyMasterSchema
>;
export type ResultGetGroupedSupportCompanies = Result<
  GetGroupedSupportCompanies,
  AppError
>;

type GetGroupedSupportCompaniesParams = {
  version?: string;
  queryParams: {
    q?: string;
  };
};

export const getGroupedSupportCompanies = async ({
  version = DEFAULT_API_VERSION,
  queryParams: {q},
}: GetGroupedSupportCompaniesParams): Promise<ResultGetGroupedSupportCompanies> => {
  const path = `/${version}/companies/supported`;
  const result: Result<GetGroupedSupportCompanies, AppError> =
    await defaultClient.get(
      createPathWithQuery(path, {q: q ?? ''}),
      GetGroupedSupportCompanyMasterSchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetGroupedSupportCompaniesOptions = {
  config?: UseQueryOptions<ResultGetGroupedSupportCompanies>;
} & GetGroupedSupportCompaniesParams;

export const useGetGroupedSupportCompanies = ({
  version = DEFAULT_API_VERSION,
  config,
  queryParams,
}: UseGetGroupedSupportCompaniesOptions) => {
  return useQuery<ResultGetGroupedSupportCompanies>({
    ...config,
    queryKey: ['getGroupedSupportCompanies', queryParams.q],
    async queryFn() {
      return getGroupedSupportCompanies({version, queryParams});
    },
  });
};

export type GetFollowAdCompanyList = z.infer<typeof FollowAdCompanyListSchema>;
export type ResultGetFollowAdCompanyList = Result<
  GetFollowAdCompanyList,
  AppError
>;

type GetFollowAdCompaniesQueryParams = {
  limit?: number;
  contractOnly?: boolean;
  gy?: number;
  minEmployeeCount?: number;
  maxEmployeeCount?: number;
  workAttractiveConditionIDs?: number[];
};

type GetFollowAdCompaniesParams = {
  version?: string;
  queryParams: GetFollowAdCompaniesQueryParams;
};

export const getFollowAdCompanies = async ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetFollowAdCompaniesParams): Promise<ResultGetFollowAdCompanyList> => {
  const path = `/${version}/advertisements/follow`;
  const param: GetFollowAdCompaniesQueryParams = {};
  if (queryParams.gy) {
    param.gy = queryParams.gy;
  }

  if (queryParams.limit) {
    param.limit = queryParams.limit;
  }

  if (queryParams.contractOnly) {
    param.contractOnly = queryParams.contractOnly;
  }

  if (queryParams.minEmployeeCount) {
    param.minEmployeeCount = queryParams.minEmployeeCount;
  }

  if (queryParams.maxEmployeeCount) {
    param.maxEmployeeCount = queryParams.maxEmployeeCount;
  }

  if (queryParams.workAttractiveConditionIDs) {
    param.workAttractiveConditionIDs = queryParams.workAttractiveConditionIDs;
  }

  const result: Result<GetFollowAdCompanyList, AppError> =
    await defaultClient.get(
      createPathWithQuery(path, param, 'none'),
      FollowAdCompanyListSchema,
    );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetFollowAdCompaniesOptions = {
  config?: UseQueryOptions<ResultGetFollowAdCompanyList>;
} & GetFollowAdCompaniesParams;

export const useGetFollowAdCompanies = ({
  version = DEFAULT_API_VERSION,
  config,
  queryParams,
}: UseGetFollowAdCompaniesOptions) => {
  return useQuery<ResultGetFollowAdCompanyList>({
    ...config,
    queryKey: ['getFollowAdCompanies', {...queryParams}],
    async queryFn() {
      return getFollowAdCompanies({version, queryParams});
    },
  });
};

export type UsersMeFollowedCompaniesResponseType = z.infer<
  typeof UsersMeFollowedCompaniesResponse
>;

type GetUsersMeArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export const getUserFollowedCompanies = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetUsersMeArgs): Promise<
  ResultWithOauthToken<UsersMeFollowedCompaniesResponseType, AppError>
> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<UsersMeFollowedCompaniesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    return defaultClient.get(
      `/${version}/users/me/followed-companies`,
      UsersMeFollowedCompaniesResponse,
    );
  };

  const result = await sendApiRequest<UsersMeFollowedCompaniesResponseType>({
    request,
    cookies,
  });

  return result;
};

/**
 * GetCompanyEntries
 * 企業のエントリー情報を取得する
 * GET /3.0/companies/{companiId}/entries
 */

export type GetCompanyEntriesResponse = z.infer<typeof CompanyEntriesSchema>;
export type ResultGetCompanyEntries = ResultWithOauthToken<
  GetCompanyEntriesResponse,
  AppError
>;

type GetCompanyEntriesArgs = {
  companyId: number;
  previewCode?: string;
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export const getCompanyEntries = async ({
  companyId,
  previewCode,
  cookies,
  version = DEFAULT_API_VERSION,
}: GetCompanyEntriesArgs): Promise<ResultGetCompanyEntries> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetCompanyEntriesResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const url = previewCode
      ? `/${version}/companies/${companyId}/entries?previewCode=${previewCode}`
      : `/${version}/companies/${companyId}/entries`;
    return defaultClient.get(url, CompanyEntriesSchema);
  };

  const result = await sendApiRequest<GetCompanyEntriesResponse>({
    request,
    cookies,
    authRequired: false,
    allowReturnError: true,
  });

  return result;
};

/**
 * GetEngineerCompanyList
 */

export type EngineerCompany = z.infer<typeof EngineerCompanySchema>;
export type GetEngineerCompanyListResponse = z.infer<
  typeof EngineerCompanyListSchema
>;
export type ResultGetEngineerCompanyList = Result<
  GetEngineerCompanyListResponse,
  AppError
>;

type GetEngineerCompanyListArgs = {
  version?: string;
  queryParams: {
    limit: number;
    page: number;
    minEmployeeCount?: number;
    maxEmployeeCount?: number;
    workAttractiveConditionIDs?: number[];
  };
};

export const getEngineerCompanyList = async ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetEngineerCompanyListArgs): Promise<ResultGetEngineerCompanyList> => {
  const path = createPathWithQuery(
    `/${version}/companies/engineer`,
    {...queryParams},
    'none',
  );
  const result: Result<GetEngineerCompanyListResponse, AppError> =
    await defaultClient.get(path, EngineerCompanyListSchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetEngineerCompanyListOptions = {
  config?: UseQueryOptions<ResultGetEngineerCompanyList>;
} & GetEngineerCompanyListArgs;

export const useGetEngineerCompanyList = ({
  queryParams,
  config,
}: UseGetEngineerCompanyListOptions) => {
  return useQuery<ResultGetEngineerCompanyList>({
    ...config,
    queryKey: ['engineerCompanies'],
    async queryFn() {
      return getEngineerCompanyList({queryParams});
    },
  });
};

export const useInfiniteEngineerCompanyList = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: UseGetEngineerCompanyListOptions): UseInfiniteQueryResult<
  GetEngineerCompanyListResponse,
  AppError
> => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useInfiniteQuery(
    ['engineerCompanies', queryParams],
    async ({pageParam = 1}) => {
      const result = await getEngineerCompanyList({
        version,
        queryParams: {
          ...queryParams,
          page: pageParam,
        },
      });

      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return getEngineerCompaniesFromPages(result);
    },
    {
      getNextPageParam: (lastPage) => ({
        page: lastPage?.nextPage ?? 0,
      }),
      enabled: config?.enabled,
    },
  );
};

type GetTopCompanyListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  queryParams: {
    limit: number;
    is_only_top: boolean;
  };
};

export type GetTopCompanyListResponse = z.infer<typeof TopCompanyListSchema>;
export type ResultGetTopCompanyList = ResultWithOauthToken<
  GetTopCompanyListResponse,
  AppError
>;

export const getTopCompanyList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetTopCompanyListArgs): Promise<ResultGetTopCompanyList> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetTopCompanyListResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(`/${version}/companies/top`, {
      ...queryParams,
    });
    return defaultClient.get(path, TopCompanyListSchema);
  };

  const result = await sendApiRequest<GetTopCompanyListResponse>({
    request,
    cookies,
    authRequired: false,
  });

  return result;
};

type GetIndustryMaster = z.infer<typeof GetIndustryMasterSchema>;
export type ResultGetIndustryMaster = Result<GetIndustryMaster, AppError>;

type GetIndustryMasterParams = {
  version?: string;
  queryParams: {
    q?: string;
    limit?: number;
  };
};

export const getIndustryMaster = async ({
  version = DEFAULT_API_VERSION,
  queryParams: {q, limit},
}: GetIndustryMasterParams): Promise<ResultGetIndustryMaster> => {
  const params = {
    q: q ?? '',
    ...(limit ? {limit} : {}),
  };
  const path = createPathWithQuery(
    `/${version}/service/master/industries`,
    params,
  );
  const result: Result<GetIndustryMaster, AppError> = await defaultClient.get(
    path,
    GetIndustryMasterSchema,
  );
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type GetCompanyEntriesOptions = {
  config?: UseQueryOptions<ResultGetCompanyEntries>;
} & Omit<GetCompanyEntriesArgs, 'cookies'>;

export const useGetCompanyEntries = ({
  companyId,
  previewCode,
  version = DEFAULT_API_VERSION,
  config,
}: GetCompanyEntriesOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultGetCompanyEntries>({
    ...config,
    queryKey: ['getCompanyEntries', companyId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getCompanyEntries({
        companyId,
        previewCode,
        cookies,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetCompanyOrientations
 * 企業の説明会情報を取得する
 * GET /3.0/companies/{companiId}/orientations
 * @param companyId
 * @param previewCode
 */

export type GetCompanyOrientationsResponse = z.infer<
  typeof CompanyOrientationsSchema
>;
export type ResultGetCompanyOrientations = ResultWithOauthToken<
  GetCompanyOrientationsResponse,
  AppError
>;

type GetCompanyOrientationsArgs = {
  companyId: number;
  previewCode?: string;
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export const getCompanyOrientations = async ({
  companyId,
  previewCode,
  cookies,
  version = DEFAULT_API_VERSION,
}: GetCompanyOrientationsArgs): Promise<ResultGetCompanyOrientations> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetCompanyOrientationsResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const url = previewCode
      ? `/${version}/companies/${companyId}/orientations?previewCode=${previewCode}`
      : `/${version}/companies/${companyId}/orientations`;
    return defaultClient.get(url, CompanyOrientationsSchema);
  };

  const result = await sendApiRequest<GetCompanyOrientationsResponse>({
    request,
    cookies,
    authRequired: false,
    allowReturnError: true,
  });

  return result;
};

type UseGetTopCompanyListOptions = {
  config?: UseQueryOptions<ResultGetTopCompanyList>;
} & Omit<GetTopCompanyListArgs, 'cookies'>;

export const useTopCompanyList = ({
  queryParams,
  config,
}: UseGetTopCompanyListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultGetTopCompanyList>({
    ...config,
    queryKey: ['topCompanies'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getTopCompanyList({cookies, queryParams});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

type UseGetIndustryMasterOptions = {
  config?: UseQueryOptions<ResultGetIndustryMaster>;
} & GetIndustryMasterParams;

export const useGetIndustryMaster = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: UseGetIndustryMasterOptions) => {
  return useQuery<ResultGetIndustryMaster>({
    ...config,
    queryKey: ['getIndustryMaster', queryParams.q, queryParams.limit],
    async queryFn() {
      return getIndustryMaster({version, queryParams});
    },
  });
};

// GET /service/master/grouped-companies
export type GetGroupedCompanies = z.infer<typeof GetGroupedCompanyMasterSchema>;
export type ResultGetGroupedCompanies = Result<GetGroupedCompanies, AppError>;

type GetGroupedCompaniesParams = {
  version?: string;
  queryParams: {
    q?: string;
    limit?: number;
  };
};

export const getGroupedCompanies = async ({
  version = DEFAULT_API_VERSION,
  queryParams: {q, limit},
}: GetGroupedCompaniesParams): Promise<ResultGetGroupedCompanies> => {
  const params = {
    q: q ?? '',
    ...(limit ? {limit} : {}),
  };
  const path = createPathWithQuery(
    `/${version}/service/master/grouped-companies`,
    params,
  );
  const result: Result<GetGroupedCompanies, AppError> = await defaultClient.get(
    path,
    GetGroupedCompanyMasterSchema,
  );

  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type GetCompanyOrientationsOptions = {
  config?: UseQueryOptions<ResultGetCompanyOrientations>;
} & Omit<GetCompanyOrientationsArgs, 'cookies'>;

export const useGetCompanyOrientations = ({
  companyId,
  previewCode,
  version = DEFAULT_API_VERSION,
  config,
}: GetCompanyOrientationsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultGetCompanyOrientations>({
    ...config,
    queryKey: ['getCompanyOrientations', companyId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getCompanyOrientations({
        companyId,
        previewCode,
        cookies,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

type UseGetCompaniesListOptions = {
  companyIds: number[];
  enabled?: boolean;
  config?: {refetchOnWindowFocus?: boolean};
  version?: string;
};

// useGetCompanyEntries と同じ queryKey を使い、企業ページの本選考一覧とキャッシュを共有する
export const useGetCompaniesEntries = ({
  companyIds,
  enabled = true,
  config,
  version = DEFAULT_API_VERSION,
}: UseGetCompaniesListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQueries({
    queries: companyIds.map((companyId) => ({
      ...config,
      queryKey: ['getCompanyEntries', companyId],
      enabled,
      async queryFn(): Promise<ResultGetCompanyEntries> {
        const cookies = parseCookies();
        const result = await getCompanyEntries({companyId, cookies, version});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    })),
  });
};

// useGetCompanyOrientations と同じ queryKey を使い、企業ページの説明会一覧とキャッシュを共有する
export const useGetCompaniesOrientations = ({
  companyIds,
  enabled = true,
  config,
  version = DEFAULT_API_VERSION,
}: UseGetCompaniesListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQueries({
    queries: companyIds.map((companyId) => ({
      ...config,
      queryKey: ['getCompanyOrientations', companyId],
      enabled,
      async queryFn(): Promise<ResultGetCompanyOrientations> {
        const cookies = parseCookies();
        const result = await getCompanyOrientations({
          companyId,
          cookies,
          version,
        });
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    })),
  });
};

type UseGetGroupedCompaniesOptions = {
  config?: UseQueryOptions<ResultGetGroupedCompanies>;
} & GetGroupedCompaniesParams;

export const useGetGroupedCompanies = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: UseGetGroupedCompaniesOptions) => {
  return useQuery<ResultGetGroupedCompanies>({
    ...config,
    queryKey: ['getGroupedCompanies', queryParams.q, queryParams.limit],
    async queryFn() {
      return getGroupedCompanies({version, queryParams});
    },
  });
};

type GetCompanyMaster = z.infer<typeof GetCompanyMasterSchema>;
export type ResultGetCompanyMaster = Result<GetCompanyMaster, AppError>;

type GetCompanyMasterParams = {
  version?: string;
  queryParams: {
    q?: string;
    limit?: number;
  };
};

export const getCompanyMaster = async ({
  version = DEFAULT_API_VERSION,
  queryParams: {limit},
}: GetCompanyMasterParams): Promise<ResultGetCompanyMaster> => {
  const params = {
    q: '',
    ...(limit ? {limit} : {}),
  };
  const path = createPathWithQuery(`/${version}/master/companies`, params);
  const result: Result<GetCompanyMaster, AppError> = await defaultClient.get(
    path,
    GetCompanyMasterSchema,
  );
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetCompanyMasterOptions = {
  config?: UseQueryOptions<ResultGetCompanyMaster>;
} & GetCompanyMasterParams;

export const useGetCompanyMaster = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: UseGetCompanyMasterOptions) => {
  return useQuery<ResultGetCompanyMaster>({
    ...config,
    queryKey: ['getCompanyMaster', queryParams.q, queryParams.limit],
    async queryFn() {
      return getCompanyMaster({version, queryParams});
    },
  });
};

// GET /work_attractive_conditions
export type ResultGetWorkAttractiveConditions = Result<
  GetWorkAttractiveConditionsType,
  AppError
>;

export const getWorkAttractiveConditions = async ({
  version = DEFAULT_API_VERSION,
}): Promise<ResultGetWorkAttractiveConditions> => {
  const path = `/${version}/work_attractive_conditions`;
  const result: Result<GetWorkAttractiveConditionsType, AppError> =
    await defaultClient.get(path, GetWorkAttractiveConditionsSchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

// GET /companies/custom-tags
type GetCustomTagsResponse = z.infer<typeof GetCustomTagsSchema>;
export type ResultGetCustomTags = Result<GetCustomTagsResponse, AppError>;

type GetCustomTagsArgs = {
  version?: string;
  queryParams: {
    companyLimit: number;
    workAttractiveConditionIDs?: number[];
    minEmployeeCount?: number;
    maxEmployeeCount?: number;
  };
};

export const getCustomTags = async ({
  version = DEFAULT_API_VERSION,
  queryParams,
}: GetCustomTagsArgs): Promise<ResultGetCustomTags> => {
  const path = createPathWithQuery(
    `/${version}/companies/custom-tags`,
    {...queryParams},
    'none',
  );
  const result: Result<GetCustomTagsResponse, AppError> =
    await defaultClient.get(path, GetCustomTagsSchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetCustomTagsOptions = {
  config?: UseQueryOptions<ResultGetCustomTags>;
} & GetCustomTagsArgs;

export const useGetCustomTags = ({
  version = DEFAULT_API_VERSION,
  queryParams,
  config,
}: UseGetCustomTagsOptions) => {
  return useQuery<ResultGetCustomTags>({
    ...config,
    queryKey: ['getCustomTags', queryParams],
    async queryFn() {
      return getCustomTags({version, queryParams});
    },
  });
};

// GET /companies/custom-tags/:id

type CustomTagWithCompany = z.infer<typeof CustomTagWithCompanySchema>;
export type ResultGetCustomTagWithCompany = Result<
  CustomTagWithCompany,
  AppError
>;

type GetCustomTagArgs = {
  version?: string;
  id: number;
  queryParams: {
    companyLimit: number;
    workAttractiveConditionIDs?: number[];
    minEmployeeCount?: number;
    maxEmployeeCount?: number;
  };
};

export const getCustomTag = async ({
  version = DEFAULT_API_VERSION,
  id,
  queryParams,
}: GetCustomTagArgs): Promise<ResultGetCustomTagWithCompany> => {
  const path = createPathWithQuery(
    `/${version}/companies/custom-tags/${id}`,
    {...queryParams},
    'none',
  );
  const result: Result<CustomTagWithCompany, AppError> =
    await defaultClient.get(path, CustomTagWithCompanySchema);
  if (!result.ok) {
    throw new Error(result.error);
  }

  return result;
};

type UseGetCustomTagOptions = {
  config?: UseQueryOptions<ResultGetCustomTagWithCompany>;
} & GetCustomTagArgs;

export const useGetCustomTag = ({
  version = DEFAULT_API_VERSION,
  id,
  queryParams,
  config,
}: UseGetCustomTagOptions) => {
  return useQuery<ResultGetCustomTagWithCompany>({
    ...config,
    queryKey: ['getCustomTag', id, queryParams],
    async queryFn() {
      return getCustomTag({version, id, queryParams});
    },
  });
};
